import { useState, Children, useRef } from 'react'
import styled from 'styled-components'
import { Button } from '@shipt/design-system-buttons'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { useRect } from '@/features/shared/hooks/useRect'
import { Box } from '@shipt/design-system-layouts'

type Props = {
  charLimit?: number
  text: string
  className?: string
  ariaLabel?: string
  children?: React.ReactNode
}

export const LineClamp = ({
  charLimit = 100,
  text = '',
  className = '',
  ariaLabel = '',
  children,
}: Props) => {
  const childrenAsArray = Children.toArray(children)
  const shouldTruncate = text.length > charLimit || childrenAsArray.length > 0
  const [isClamped, setIsClamped] = useState(shouldTruncate)

  const truncatedText = `${text.substring(0, charLimit)}…`
  const btnLabel = isClamped ? 'Show more' : 'Show less'

  return (
    <div className={className}>
      {isClamped ? (
        <div>{truncatedText}</div>
      ) : (
        <>
          <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(text) }} />
          {children}
        </>
      )}
      {!!shouldTruncate && (
        <LineClampButton
          variant="secondary"
          size="sm"
          onClick={() => setIsClamped(!isClamped)}
          aria-label={ariaLabel ? `${btnLabel} for ${ariaLabel}` : btnLabel}
        >
          {btnLabel}
        </LineClampButton>
      )}
    </div>
  )
}

const LineClampButton = styled(Button)`
  margin-top: 1rem;
`

export const HeightLineClamp = ({
  children,
  height,
  ariaLabel,
}: {
  height: number
  children: React.ReactNode
  ariaLabel: string
}) => {
  const ref = useRef<HTMLDivElement>(null)

  const rect = useRect(ref)
  const shouldTruncate = rect && rect.height > height

  const [isTruncated, setIsTruncated] = useState(shouldTruncate)

  if (shouldTruncate && typeof isTruncated !== 'boolean') {
    setIsTruncated(true)
  }

  const btnLabel = isTruncated ? 'Show more' : 'Show less'

  return (
    <>
      <Wrapper $isTruncated={!!isTruncated} height={height}>
        <div ref={ref}>{children}</div>
      </Wrapper>
      {!!shouldTruncate && (
        <Box marginTop="xl">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsTruncated(!isTruncated)}
            aria-label={ariaLabel ? `${btnLabel} for ${ariaLabel}` : btnLabel}
          >
            {btnLabel}
          </Button>
        </Box>
      )}
    </>
  )
}

const Wrapper = styled.div<{ $isTruncated: boolean; height: number }>`
  max-height: ${({ $isTruncated, height }) => ($isTruncated ? `${height}px` : 'auto')};
  overflow: hidden;
`
